import React, { useCallback, useEffect, useState } from "react";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

const todayString = () => new Date().toISOString().slice(0, 10);
const startOfMonthString = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

export default function AdminActivityTimeline() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [expandedRaw, setExpandedRaw] = useState({});
  const [filters, setFilters] = useState({
    q: "",
    action: "",
    entity_type: "",
    from: startOfMonthString(),
    to: todayString(),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/audit-logs", {
        params: {
          ...filters,
          page,
          per_page: 30,
        },
      });
      setLogs(Array.isArray(res.data?.data) ? res.data.data : []);
      setLastPage(Number(res.data?.last_page || 1));
    } catch (error) {
      console.error(error);
      setLogs([]);
      setLastPage(1);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get("/admin/audit-logs/export", {
        params: filters,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `activity-timeline-${todayString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const toLabel = (key) =>
    String(key || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const renderReadableDetails = (details) => {
    if (!details || typeof details !== "object") return null;
    const entries = Object.entries(details).filter(([, value]) => value !== null && value !== undefined && value !== "");
    if (entries.length === 0) return null;

    return (
      <div className="mt-3 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-slate-200">
        {entries.map(([key, value]) => (
          <p key={key} className="mb-1 last:mb-0">
            <span className="font-semibold text-slate-300">{toLabel(key)}:</span>{" "}
            {typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true" src={bgVideo} autoPlay muted loop playsInline className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 border-b border-white/10 pb-5">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
            Admin Activity
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">System Timeline</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
            Unified audit trail for actions across reports, payments, announcements, tenants, and properties.
          </p>
        </div>

        <div className="mb-5 grid gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl xl:grid-cols-[minmax(0,1fr)_220px_220px_auto_auto] xl:items-center">
          <input
            value={filters.q}
            onChange={(e) => {
              setPage(1);
              setFilters((prev) => ({ ...prev, q: e.target.value }));
            }}
            className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            placeholder="Search actor, email, action, entity"
          />
          <input
            value={filters.action}
            onChange={(e) => {
              setPage(1);
              setFilters((prev) => ({ ...prev, action: e.target.value }));
            }}
            className="rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            placeholder="Action filter"
          />
          <input
            value={filters.entity_type}
            onChange={(e) => {
              setPage(1);
              setFilters((prev) => ({ ...prev, entity_type: e.target.value }));
            }}
            className="rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            placeholder="Entity type"
          />
          <input
            type="date"
            value={filters.from}
            onChange={(e) => {
              setPage(1);
              setFilters((prev) => ({ ...prev, from: e.target.value }));
            }}
            className="rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => {
              setPage(1);
              setFilters((prev) => ({ ...prev, to: e.target.value }));
            }}
            className="rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
        </div>

        <div className="mb-4 flex justify-end">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {exporting ? "Exporting..." : "Export Timeline CSV"}
          </button>
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading activity...
          </div>
        ) : logs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/15 bg-slate-950/70 py-10 text-center text-slate-300">
            No activity logs found for the selected filters.
          </div>
        ) : (
          <div className="grid gap-3">
            {logs.map((log) => (
              <article key={log.id} className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-white">{log.action}</p>
                    <p className="mt-1 text-xs text-slate-400">
                      {log.entity_type || "Unknown"} #{log.entity_id || "-"}
                    </p>
                  </div>
                  <div className="text-xs text-slate-400 md:text-right">
                    <p>{log.actor?.name || "System"} ({log.actor_role || "N/A"})</p>
                    <p>{new Date(log.created_at).toLocaleString()}</p>
                  </div>
                </div>
                {renderReadableDetails(log.details)}
                {log.details && (
                  <div className="mt-2">
                    <button
                      onClick={() =>
                        setExpandedRaw((prev) => ({ ...prev, [log.id]: !prev[log.id] }))
                      }
                      className="rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-300 transition hover:bg-white/10"
                    >
                      {expandedRaw[log.id] ? "Hide Raw Details" : "View Raw Details"}
                    </button>
                    {expandedRaw[log.id] && (
                      <pre className="mt-2 overflow-x-auto rounded-lg border border-white/10 bg-slate-950 p-3 text-xs text-slate-300">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-200 disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm text-slate-300">
            Page {page} of {lastPage}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(lastPage, prev + 1))}
            disabled={page >= lastPage}
            className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
